import { createContext, useState } from 'react'
import { IAudit } from '../../../../../app/entities/Audit'
import {
  IAuditImage,
  IAuditSectionData,
  IAuditState,
  STATUS,
} from '../../types/audit'
import { fetchData } from '../../utils/fetch'
import {
  getAuditByObjectId,
  getAuditSections,
  getDvirImages,
  getExampleImages,
  getInstallationImages,
  putAudit,
} from './api'
import { useAuditConfig } from '../audit-config-context/AuditConfigContext'
import { IOrderVehicle } from '../../../../../app/entities/OrderVehicle'
import { IOrder } from '../../../../../app/entities/Order'
import { getNextSection } from '../../../audits/utils/getAuditSection'

interface IContextProps {
  auditData: IAudit | null
  setAuditData: Function
  auditSectionData: { [key: string]: IAuditSectionData }
  setAuditSectionData: Function
  technicianImages: IAuditImage[]
  setTechnicianImages: Function
  auditStatus: STATUS | null
  setAuditStatus: Function
  auditState: IAuditState | null
  setAuditState: Function
  isAuditDataLoading: boolean
  setIsAuditDataLoading: Function
  orderVehicle: IOrderVehicle | null
  order: IOrder | null
  technician: string
  progress: string
  setProgress: Function
  sections: { [key: string]: string }
  setSections: Function
  clearAuditData: () => void
  saveAuditData: (
    newProgress: string,
    newAuditState: IAuditState | null,
  ) => Promise<void>
  queryAuditData: Function
}

interface IProviderProps {
  children: React.ReactNode
}

export const AuditContext = createContext<IContextProps>({} as IContextProps)

export function AuditContextProvider({ children }: IProviderProps) {
  // Get audit configs from context
  const COMPLETED_INSTALLATION_SECTIONS = useAuditConfig(STATUS.COMPLETED_INSTALLATION).SECTIONS
  const ON_SITE_CANCELLATION_SECTIONS = useAuditConfig(STATUS.ON_SITE_CANCELLATION).SECTIONS
  const DAILY_DVIR_SECTIONS = useAuditConfig(STATUS.DAILY_DVIR).SECTIONS
  const WEEKLY_DVIR_SECTIONS = useAuditConfig(STATUS.WEEKLY_DVIR).SECTIONS

  // Build full configs object for getNextSection
  const configs = {
    [STATUS.COMPLETED_INSTALLATION]: useAuditConfig(STATUS.COMPLETED_INSTALLATION),
    [STATUS.ON_SITE_CANCELLATION]: useAuditConfig(STATUS.ON_SITE_CANCELLATION),
    [STATUS.DAILY_DVIR]: useAuditConfig(STATUS.DAILY_DVIR),
    [STATUS.WEEKLY_DVIR]: useAuditConfig(STATUS.WEEKLY_DVIR),
  }

  const [auditData, setAuditData] = useState<IAudit | null>(null)
  const [auditSectionData, setAuditSectionData] = useState<{
    [key: string]: IAuditSectionData
  }>({})
  const [technician, setTechnician] = useState<string>('')
  const [technicianImages, setTechnicianImages] = useState<IAuditImage[]>([])
  const [auditStatus, setAuditStatus] = useState<STATUS | null>(null)
  const [auditState, setAuditState] = useState<IAuditState | null>(null)
  const [isAuditDataLoading, setIsAuditDataLoading] = useState<boolean>(false)
  const [progress, setProgress] = useState<string>('')
  const [sections, setSections] = useState<{ [key: string]: string }>({})
  const [orderVehicle, setOrderVehicle] = useState<IOrderVehicle | null>(null)
  const [order, setOrder] = useState<IOrder | null>(null)

  function clearAuditData() {}

  async function queryAuditData(audtiObjectId: string) {
    setIsAuditDataLoading(true)
    try {
      const auditResults = await fetchData<IAudit>(
        getAuditByObjectId(audtiObjectId),
      )
      if (!auditResults) {
        throw new Error('No audit data found')
      }
      setAuditData(auditResults)
      const {
        status,
        orderVehicle: newOrderVehicle,
        dvir,
        state,
        progress: newProgress,
        technician,
      } = auditResults

      setAuditStatus(status)
      setAuditState(state)
      setTechnician(technician?.name || '')
      setOrderVehicle(newOrderVehicle)
      setOrder(newOrderVehicle?.order || null)

      switch (status) {
        case STATUS.COMPLETED_INSTALLATION: {
          const [installationImages, sections] = await Promise.all([
            fetchData<IAuditImage[]>(
              getInstallationImages(newOrderVehicle?.objectId || ''),
            ),
            fetchData<{ [key: string]: IAuditSectionData }>(
              getAuditSections(auditResults.objectId),
            ),
          ])

          setTechnicianImages(installationImages)
          setAuditSectionData(sections)

          // Get initial progress, defaulting to first section
          let initialProgress = newProgress || COMPLETED_INSTALLATION_SECTIONS.pre_install_front_passenger
          // Only apply skipping logic if we're starting fresh (no saved progress)
          if (!newProgress) {
            initialProgress = getNextSection(initialProgress, state, status, configs, true, true)
          }

          setProgress(initialProgress)
          setSections(COMPLETED_INSTALLATION_SECTIONS)
          break
        }
        case STATUS.ON_SITE_CANCELLATION: {
          const [installationImages, sections] = await Promise.all([
            fetchData<IAuditImage[]>(
              getInstallationImages(newOrderVehicle?.objectId || ''),
            ),
            fetchData<{ [key: string]: IAuditSectionData }>(
              getAuditSections(auditResults.objectId),
            ),
          ])

          setTechnicianImages(installationImages)
          setAuditSectionData(sections)

          // Get initial progress, defaulting to first section
          let initialProgress = newProgress || ON_SITE_CANCELLATION_SECTIONS.cancellation_condition
          // Only apply skipping logic if we're starting fresh (no saved progress)
          if (!newProgress) {
            initialProgress = getNextSection(initialProgress, state, status, configs, true, true)
          }

          setProgress(initialProgress)
          setSections(ON_SITE_CANCELLATION_SECTIONS)
          break
        }
        case STATUS.DAILY_DVIR: {
          const [dvirImages, sections] = await Promise.all([
            fetchData<IAuditImage[]>(getDvirImages(dvir?.objectId || '')),
            fetchData<{ [key: string]: IAuditSectionData }>(
              getAuditSections(auditResults.objectId),
            ),
          ])

          setTechnicianImages(dvirImages)
          setAuditSectionData(sections)

          // Get initial progress, defaulting to first section
          let initialProgress = newProgress || DAILY_DVIR_SECTIONS.van_external_images
          // Only apply skipping logic if we're starting fresh (no saved progress)
          if (!newProgress) {
            initialProgress = getNextSection(initialProgress, state, status, configs, true, true)
          }

          setProgress(initialProgress)
          setSections(DAILY_DVIR_SECTIONS)
          break
        }
        case STATUS.WEEKLY_DVIR: {
          const [dvirImages, sections] = await Promise.all([
            fetchData<IAuditImage[]>(getDvirImages(dvir?.objectId || '')),
            fetchData<{ [key: string]: IAuditSectionData }>(
              getAuditSections(auditResults.objectId),
            ),
          ])

          setTechnicianImages(dvirImages)
          setAuditSectionData(sections)

          // Get initial progress, defaulting to first section
          let initialProgress = newProgress || WEEKLY_DVIR_SECTIONS.front_of_van
          // Only apply skipping logic if we're starting fresh (no saved progress)
          if (!newProgress) {
            initialProgress = getNextSection(initialProgress, state, status, configs, true, true)
          }

          setProgress(initialProgress)
          setSections(WEEKLY_DVIR_SECTIONS)
          break
        }
      }
    } catch (error) {
      console.error('Failed to fetch audit data:', error)
      throw error
    } finally {
      setIsAuditDataLoading(false)
    }
  }

  async function saveAuditData(
    newProgress: string,
    newAuditState: IAuditState | null,
  ) {
    if (!auditData) return
    auditData.progress = newProgress
    auditData.state = newAuditState
    await Promise.all([fetchData<IAudit>(putAudit(auditData))])
  }

  return (
    <AuditContext.Provider
      value={{
        auditData,
        setAuditData,
        auditSectionData,
        setAuditSectionData,
        technicianImages,
        setTechnicianImages,
        auditStatus,
        auditState,
        setAuditState,
        setAuditStatus,
        isAuditDataLoading,
        setIsAuditDataLoading,
        progress,
        setProgress,
        sections,
        setSections,
        clearAuditData,
        saveAuditData,
        queryAuditData,
        technician,
        orderVehicle,
        order,
      }}
    >
      {children}
    </AuditContext.Provider>
  )
}
